import {
  type FC,
  type ReactNode,
  useCallback,
  useSyncExternalStore,
} from "react";
import { useAudioPlayer } from "./hook.js";

export interface AudioPlayerTriggerProps {
  /** The source this fragment belongs to. */
  sourceKey: string;
  /** The start time of this fragment. Defaults to 0. */
  time?: number;
  /** The start time of the next fragment. Used to determine whether this fragment is playing. Defaults to Infinity. */
  nextTime?: number | undefined;
  children: (props: {
    /** Whether this fragment is playing. */
    isPlaying: boolean;
    onPress: (_event?: unknown, action?: "play" | "pause") => void;
  }) => ReactNode;
}

export const AudioPlayerTrigger: FC<AudioPlayerTriggerProps> = (props) => {
  const { sourceKey, time = 0, nextTime = Infinity } = props;

  const audioPlayer = useAudioPlayer();
  const nativeAudio = audioPlayer.nativeAudio;
  const isPlayingSource = audioPlayer.currentSourceKey === sourceKey;

  // Do this all in the same hook to avoid unnecessary re-renders, since this will be used for many fragments on the page
  const isPlayingFragment = useSyncExternalStore(
    useCallback(
      (onStoreChange: () => void) => {
        // We don't need to subscribe/unsubscribe if we're not playing our audio source
        if (!nativeAudio || !isPlayingSource) {
          return () => {};
        }

        nativeAudio.addEventListener("emptied", onStoreChange);
        nativeAudio.addEventListener("play", onStoreChange);
        nativeAudio.addEventListener("pause", onStoreChange);
        nativeAudio.addEventListener("timeupdate", onStoreChange);

        return () => {
          nativeAudio.removeEventListener("emptied", onStoreChange);
          nativeAudio.removeEventListener("play", onStoreChange);
          nativeAudio.removeEventListener("pause", onStoreChange);
          nativeAudio.removeEventListener("timeupdate", onStoreChange);
        };
      },
      [nativeAudio, isPlayingSource],
    ),
    () => {
      if (!nativeAudio) {
        return false;
      }

      const isPlaying = !nativeAudio.paused;
      const isInTimeRange =
        nativeAudio.currentTime >= time && nativeAudio.currentTime < nextTime;

      return isPlaying && isPlayingSource && isInTimeRange;
    },
  );

  const onPress = useCallback(
    (_event?: unknown, action?: "play" | "pause") => {
      if (action ? action === "pause" : isPlayingFragment) {
        nativeAudio?.pause();
      } else {
        audioPlayer.play(sourceKey, time);
      }
    },
    [audioPlayer, isPlayingFragment, nativeAudio, sourceKey, time],
  );

  return props.children({
    isPlaying: isPlayingFragment,
    onPress,
  });
};
